// ==========================================
// src/hooks/useBusiness.js - Business management hook
// ==========================================

import { useCallback, useEffect, useState } from 'react';
import { BusinessProfile } from '../models/BusinessProfile';
import { FirebaseService } from '../services/FirebaseService';
import { useAuth } from './useAuth';

export const useBusiness = () => {
  const [businessProfile, setBusinessProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  // Load business profile
  const loadBusinessProfile = useCallback(async () => {
    if (!user?.businessId) {
      setBusinessProfile(null);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const firebaseData = await FirebaseService.getBusinessProfile(user.businessId);
      
      if (firebaseData) {
        const businessModel = BusinessProfile.fromFirebaseData(user.businessId, firebaseData);
        setBusinessProfile(businessModel);
      }
      
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load business profile:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.businessId]);

  useEffect(() => {
    loadBusinessProfile();
  }, [loadBusinessProfile]);

  // Update business basic info
  const updateBasicInfo = async (updates) => {
    if (!businessProfile) return { success: false, error: 'No business profile loaded' };

    try {
      const validation = businessProfile.updateBasicInfo(updates);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      await FirebaseService.updateBusinessProfile(businessProfile.id, businessProfile.toFirebaseData());
      setBusinessProfile({ ...businessProfile });
      
      return { success: true };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Update working hours
  const updateWorkingHours = async (newHours) => {
    if (!businessProfile) return { success: false, error: 'No business profile loaded' };

    try {
      const validation = businessProfile.updateWorkingHours(newHours);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      await FirebaseService.updateBusinessProfile(businessProfile.id, businessProfile.toFirebaseData());
      setBusinessProfile({ ...businessProfile });
      
      return { success: true };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Update settings
  const updateSettings = async (settingsUpdates) => {
    if (!businessProfile) return { success: false, error: 'No business profile loaded' };

    try {
      businessProfile.updateSettings(settingsUpdates);
      
      await FirebaseService.updateBusinessProfile(businessProfile.id, businessProfile.toFirebaseData());
      setBusinessProfile({ ...businessProfile });
      
      return { success: true };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Update subscription tier
  const updateTier = async (newTier, billingCycle) => {
    if (!businessProfile) return { success: false, error: 'No business profile loaded' };

    try {
      const validation = businessProfile.updateTier(newTier, billingCycle);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      await FirebaseService.updateBusinessProfile(businessProfile.id, businessProfile.toFirebaseData());
      setBusinessProfile({ ...businessProfile });
      
      return { success: true };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  return {
    businessProfile,
    loading,
    error,
    updateBasicInfo,
    updateWorkingHours,
    updateSettings,
    updateTier,
    reload: loadBusinessProfile
  };
};
